import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../redux/authSlice';
import { HashRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Navbar from '../../component/Navbar/Navbar';
import SideNav from '../../component/Sidebar/Sidebar';
import Loading from '../../component/Loading/Loading';

// Pages - Import all your pages
import AdminDashboard from '../../Pages/Dashboard/Dashboard';
import CommunityDetails from '../../Pages/CommunityDetails/CommunityDetails';
import BankDetails from '../../Pages/CommunityDetails/BankDetails/BankDetails';
import AmenityBooking from '../../Pages/AmenityBooking/AmenityBooking';
import CommunityDocuments from '../../Pages/CommunityDocuments/CommunityDocuments';
import Reports from '../../Pages/Reports/Reports';
import Settings from '../../Pages/Settings/Settings';
import Newpublishing from '../../Pages/Newpublishing/Newpublishing';
import PaymentsDetails from '../../Pages/PaymentsDetails/PaymentsDetails';
import FAQS from '../../Pages/FAQS/FAQS';
import ServiceRequest from '../../Pages/ServiceRequest/ServiceRequest';
import CreateNew from '../../Pages/Newpublishing/CreateNew/CreateNew';
import DateRangePicker from '../../utils/DateRangePicker/DateRangePicker';
import AddCommunity from '../../Pages/AddCommunity/AddCommunity';
import ContractGeneration from '../../Pages/ContractGeneration/ContractGeneration';
import ContractList from '../../Pages/ContractList/ContractList';
import ChangePassword from "../../component/ChangePassword/ChangePassword";
import Dashboard from '../../Pages/Dashboard/Dashboard';
import MemberProfile from "../../Pages/MemberProfile/MemberProfile";
import EditProfile from "../../Pages/MemberProfile/EditProfile/EditProfile";
import HoaAdminNotification from '../../Pages/HoaAdminNotification/HoaAdminNotification';
import UServiceRequest from "../../Pages/ServiceRequest/ServiceRequest";
import Payments from "../../Pages/Payments/Payments";
import UAmenityBooking from "../../Pages/AmenityBooking/AmenityBooking";
import Violations from "../../Pages/Violations/Violations";
import AssociationInformation from "../../Pages/AssociationInformation/AssociationInformation";
import ForgotPassword from '../../component/ForgotPassword/ForgotPassword';
import SetTimeZone from '../../Pages/SetTimeZone/SetTimeZone';
import EmployeeList from '../../Pages/EmployeeList/EmployeeList';
import EmployeePage from '../../Pages/AddEmployee/AddEmployee';
import AddEmployee from '../../Pages/AddEmployee/AddEmployee';
import EditContract from '../../Pages/ContractList/EditContract/EditContract';
import ContactList from '../../Pages/ContactList/ContactList';
import LoadingComman from "../../component/LoadingComman/LoadingComman";
import AddPlan from '../../Pages/AddPlan/AddPlan';
import PlanList from '../../Pages/PlanList/PlanList';
import CommunityList from '../../Pages/CommunityList/CommunityList';
import AddAmenity from '../../Pages/AddAmenity/AddAmenity';
import AddAmenityType from '../../Pages/AddAmenityType/AddAmenityType';
import AddSlot from '../../Pages/AddSlot/AddSlot';
import AddServices from '../../Pages/AddServices/AddService';
import AuditLog from '../../Pages/AuditLog/AuditLog';
import AddViolation from '../../Pages/AddViolation/AddViolation';
import vendor from '../../Pages/Vendors/vendor';
import ViolationDetails from '../../Pages/Violations/ViolationDetails/ViolationDetails';
import AssignVendor from '../../Pages/ServiceRequest/AssignVendor';
import RolePageCrud from '../../Pages/RolePageCrud/RolePageCrud';
import VendorManagement from '../../Pages/VendorManagement/VendorManagement';
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import ConfirmPassword from '../../component/ConfirmPassword/ConfirmPassword';
import RolePageAllow from '../../Pages/RolePageAllow/RolePageAllow';
import Pagenation from '../../utils/Pagenation/Pagenation';
import Notification from '../../Pages/Notification/Notification';
import PaymentPage from '../../Pages/PaymentsDetails/PaymentPage';
import MonthlyFees from '../../Pages/MonthlyFees/MonthlyFees';

const MainRouting = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);

  const auth = useSelector(selectAuth);
  const isLoading = useSelector((state) => state.loading.isLoading);

  useEffect(() => {
    // Check if we're in mobile view
    const checkMobileView = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);

      // Auto-close sidebar on mobile when first loading
      if (isMobile) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    // Initial check
    checkMobileView();

    // Add event listener for resize
    window.addEventListener('resize', checkMobileView);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = (forceState) => {
    // If forceState is provided, use it; otherwise toggle to false
    if (typeof forceState === 'boolean') {
      setIsSidebarOpen(forceState);
    } else {
      setIsSidebarOpen(false);
    }
  };

  if (!auth.isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <Router>
      <div className="flex h-screen overflow-hidden bg-gray-100">
        {/* Mobile overlay when sidebar is open */}
        {isMobileView && isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => closeSidebar(false)}
          />
        )}

        {/* Sidebar */}
        <AnimatePresence>
          <motion.div
            className={`h-screen flex-shrink-0 shadow-lg z-40 ${isMobileView ? 'fixed' : 'relative'}`}
            initial={{
              width: isMobileView ? 0 : (isSidebarOpen ? '300px' : '70px'),
              x: isMobileView && !isSidebarOpen ? '-100%' : 0
            }}
            animate={{
              width: isMobileView
                ? (isSidebarOpen ? '250px' : 0)
                : (isSidebarOpen ? '300px' : '70px'),
              x: isMobileView && !isSidebarOpen ? '-100%' : 0
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <SideNav
              isSidebarOpen={isSidebarOpen}
              closeSidebar={closeSidebar}
              isCollapsed={!isSidebarOpen}
              toggleSidebar={toggleSidebar}
            />
          </motion.div>
        </AnimatePresence>

        {/* Main Content */}
        <motion.div
          className="flex flex-col flex-grow overflow-hidden"
          initial={{
            width: isMobileView ? '100%' : (isSidebarOpen ? 'calc(100% - 250px)' : 'calc(100% - 70px)')
          }}
          animate={{
            width: isMobileView
              ? '100%'
              : (isSidebarOpen ? 'calc(100% - 250px)' : 'calc(100% - 70px)')
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Navbar */}
          <Navbar toggleSidebar={toggleSidebar} />

          {/* Main content with auto scroll */}
          <div className="flex-grow p-3 sm:p-4 md:p-6 overflow-auto bg-gray-50">
            {isLoading ? (
              <Loading />
            ) : (
              <Switch>
                <Route exact path="/" component={AdminDashboard} />
                <Route path="/add-community" component={AddCommunity} />
                <Route path="/community-details" component={CommunityDetails} />
                <Route path="/bank-details" component={BankDetails} />
                <Route path="/service-request" component={ServiceRequest} />
                <Route path="/amenity-booking" component={AmenityBooking} />
                <Route path="/community-documents" component={CommunityDocuments} />
                <Route path="/reports" component={Reports} />
                <Route path="/settings" component={Settings} />
                <Route path="/vendor-management" component={VendorManagement} />
                <Route path="/new-publishing" component={Newpublishing} />
                <Route path="/create-new-publishing" component={CreateNew} />
                <Route path="/payments-details" component={PaymentsDetails} />
                <Route path="/faqs" component={FAQS} />
                <Route path="/date-picker" component={DateRangePicker} />
                <Route path="/contract-generations" component={ContractGeneration} />
                <Route path="/contract-list" component={ContractList} />
                <Route path="/change-password" component={ChangePassword} />
                <Route path="/edit-contract/:id" component={EditContract} />
                <Route path="/alert-msg" component={AlertMsg} />
                <Route path="/dashboard" component={Dashboard} />
                <Route path="/member-profile" component={MemberProfile} />
                <Route path="/edit-profile" component={EditProfile} />
                <Route path="/service-request" component={UServiceRequest} />
                <Route path="/payments" component={Payments} />
                <Route path="/amenity-booking" component={UAmenityBooking} />
                <Route path="/violations" component={Violations} />
                <Route path="/association-info" component={AssociationInformation} />
                <Route path="/hoa-admin-notification" component={HoaAdminNotification} />
                <Route path="/forgot-password" component={ForgotPassword} />
                <Route path="/change-password" component={ChangePassword} />
                <Route path="/time-zone" component={SetTimeZone} />

                {/* Routes for Super Admin */}
                <Route path="/superadmin/add-community" component={AddCommunity} />
                <Route path="/Contact-List" component={ContactList} />
                <Route path="/community-details/:Cid" component={CommunityDetails} />
                <Route path="/community-list" component={CommunityList} />
                <Route path="/rolepage-crud" component={RolePageCrud} />
                <Route path="/rolepage-allow" component={RolePageAllow} />

                {/* Additional routes can be added for other user roles */}
                <Route path="/employee-list" component={EmployeeList} />
                <Route path="/add-employee" component={AddEmployee} />
                <Route path="/add-plan" component={AddPlan} />
                <Route path="/plan-list" component={PlanList} />
                <Route path="/add-amenity" component={AddAmenity} />
                <Route path="/add-amenity-type" component={AddAmenityType} />
                <Route path="/add-slot" component={AddSlot} />
                <Route path="/add-service" component={AddServices} />
                <Route path="/add-violation" component={AddViolation} />
                <Route path="/vendor-display" component={vendor} />
                <Route path="/auditlog" component={AuditLog} />
                <Route path="/pagenation" component={Pagenation} />
                <Route path="/notification" component={Notification} />

                <Route path="/violation-details/:ViolationId" component={ViolationDetails} />
                <Route path="/assign-vendor/:serviceRequestId" component={AssignVendor} />
                <Route path="/payment-page" component={PaymentPage} />
                <Route path="/monthly-fees" component={MonthlyFees} />
                <Redirect to="/" />
              </Switch>
            )}
          </div>
        </motion.div>
      </div>
    </Router>
  );
};

export default MainRouting;